import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { Stack, Row, Text, Panel, List, ListItem, Avatar, Money, Divider, Button, Input, EmptyState, Tag, PlattsPicker } from '../ui';

const fmt = (n: number, d = 0) => new Intl.NumberFormat('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }).format(n);
const fmt2 = (n: number) => fmt(n, 2);

const STATUSES = [
  { v: 'opportunity',     l: 'Opportunity'     },
  { v: 'active',          l: 'In progress'     },
  { v: 'contract_signed', l: 'Contract signed' },
  { v: 'delivering',      l: 'Delivering'      },
  { v: 'completed',       l: 'Completed'       },
];

const PLATTS_BENCHMARKS = [
  'Dated Brent','ICE Brent','WTI Cushing','WTI Midland','Bonny Light','Forties','Mars',
  'Dubai','Oman','Murban','Urals',
  'JKM','TTF','NBP','Henry Hub','PVB',
  'Singapore Gasoil 10ppm','Singapore Jet','NYH RBOB','NWE Eurobob',
  'Singapore HSFO 380','NWE LSFO 0.5%',
  'Argus Premium Low Vol Coal','API2','API4','API6',
  'Other',
];

type LegEdit = {
  basis: 'fixed' | 'platts';
  fixed: string;
  benchmark: string;
  code: string;
  benchmarkPrice: string;
  differential: string;
};

function legFromDeal(deal: any, side: 'buy'|'sell'): LegEdit {
  if (side === 'buy') {
    return {
      basis: deal.buyPricingBasis ?? 'fixed',
      fixed: deal.buyPricePerUnit?.toString() ?? deal.costPerUnit?.toString() ?? '',
      benchmark: deal.buyPlattsBenchmark ?? '',
      code: deal.buyPlattsCode ?? '',
      benchmarkPrice: deal.buyPlattsBenchmarkPrice?.toString() ?? '',
      differential: deal.buyPlattsDifferential?.toString() ?? '',
    };
  }
  return {
    basis: deal.sellPricingBasis ?? deal.pricingBasis ?? 'fixed',
    fixed: deal.sellPricePerUnit?.toString() ?? deal.pricePerUnit?.toString() ?? '',
    benchmark: deal.sellPlattsBenchmark ?? deal.plattsBenchmark ?? '',
    code: deal.sellPlattsCode ?? '',
    benchmarkPrice: deal.sellPlattsBenchmarkPrice?.toString() ?? deal.plattsBenchmarkPrice?.toString() ?? '',
    differential: deal.sellPlattsDifferential?.toString() ?? deal.plattsDifferential?.toString() ?? '',
  };
}

function legEffective(leg: LegEdit) {
  if (leg.basis === 'fixed') return parseFloat(leg.fixed || '0');
  return parseFloat(leg.benchmarkPrice || '0') + parseFloat(leg.differential || '0');
}

export function DealEconomics({ dealId, deal, ledger, participants, friendById, addCost, newCost, setNewCost, expectedRevenue, totalCosts }: any) {
  const [econ, setEcon] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState({
    volumeAmount: deal.volumeAmount?.toString() ?? '',
    volumeUnit: deal.volumeUnit ?? 'bbl',
    contractFrequency: deal.contractFrequency ?? 'one-off',
    contractDurationMonths: deal.contractDurationMonths?.toString() ?? '',
    buyLeg: legFromDeal(deal, 'buy'),
    sellLeg: legFromDeal(deal, 'sell'),
  });

  async function loadEcon() {
    setBusy(true);
    try { setEcon(await api.dealEconomics(dealId)); } finally { setBusy(false); }
  }

  useEffect(() => { loadEcon(); }, [dealId]);

  async function patchEcon() {
    setBusy(true);
    try {
      const patch: any = {
        volumeAmount: parseFloat(edit.volumeAmount) || null,
        volumeUnit: edit.volumeUnit || null,
        contractFrequency: edit.contractFrequency || null,
        contractDurationMonths: edit.contractDurationMonths ? parseInt(edit.contractDurationMonths, 10) : null,
      };
      // BUY
      patch.buyPricingBasis = edit.buyLeg.basis;
      if (edit.buyLeg.basis === 'fixed') {
        patch.buyPricePerUnit = parseFloat(edit.buyLeg.fixed) || null;
        patch.buyPlattsBenchmark = null; patch.buyPlattsCode = null; patch.buyPlattsBenchmarkPrice = null; patch.buyPlattsDifferential = null;
      } else {
        patch.buyPricePerUnit = legEffective(edit.buyLeg);
        patch.buyPlattsBenchmark = edit.buyLeg.benchmark;
        patch.buyPlattsCode = edit.buyLeg.code || null;
        patch.buyPlattsBenchmarkPrice = parseFloat(edit.buyLeg.benchmarkPrice) || null;
        patch.buyPlattsDifferential = parseFloat(edit.buyLeg.differential) || 0;
      }
      patch.costPerUnit = legEffective(edit.buyLeg);
      // SELL
      patch.sellPricingBasis = edit.sellLeg.basis;
      if (edit.sellLeg.basis === 'fixed') {
        patch.sellPricePerUnit = parseFloat(edit.sellLeg.fixed) || null;
        patch.sellPlattsBenchmark = null; patch.sellPlattsCode = null; patch.sellPlattsBenchmarkPrice = null; patch.sellPlattsDifferential = null;
      } else {
        patch.sellPricePerUnit = legEffective(edit.sellLeg);
        patch.sellPlattsBenchmark = edit.sellLeg.benchmark;
        patch.sellPlattsCode = edit.sellLeg.code || null;
        patch.sellPlattsBenchmarkPrice = parseFloat(edit.sellLeg.benchmarkPrice) || null;
        patch.sellPlattsDifferential = parseFloat(edit.sellLeg.differential) || 0;
      }
      patch.pricePerUnit = legEffective(edit.sellLeg);
      // Mirror sell to legacy pricingBasis so older paths keep working
      patch.pricingBasis = edit.sellLeg.basis;

      await api.patchDeal(dealId, patch);
      window.location.reload();
    } finally { setBusy(false); }
  }

  async function setStatus(v: string) {
    setBusy(true);
    try { await api.patchDeal(dealId, { dealStatus: v }); window.location.reload(); }
    finally { setBusy(false); }
  }

  const ccy = deal.currency ?? 'USD';
  const isVolume = !!econ?.deal.volumeAmount && econ?.deal.volumeAmount > 0;
  const status = deal.dealStatus ?? 'opportunity';

  // Live preview from edit state
  const buyEff = legEffective(edit.buyLeg);
  const sellEff = legEffective(edit.sellLeg);
  const liveSpread = sellEff - buyEff;

  return (
    <Stack gap={3}>
      <Panel title="Status">
        <Row gap={0} className="overflow-x-auto">
          {STATUSES.map((s, i) => {
            const active = status === s.v;
            const passed = STATUSES.findIndex(x => x.v === status) > i;
            return (
              <button key={s.v} onClick={() => setStatus(s.v)} disabled={busy}
                className={`flex-1 px-3 py-2 hairline-r last:border-r-0 text-center text-xs tracking-tight transition-colors ${active ? 'bg-ink text-bg' : passed ? 'text-ink' : 'text-muted'} hover:bg-whisper`}>
                {s.l}
              </button>
            );
          })}
        </Row>
      </Panel>

      <Panel title="Volume & contract">
        <Row gap={2} wrap>
          <Stack gap={0.5} className="flex-1 min-w-[100px]">
            <Text variant="label">volume / shipment</Text>
            <Input type="number" value={edit.volumeAmount} onChange={e => setEdit({ ...edit, volumeAmount: e.target.value })} />
          </Stack>
          <Stack gap={0.5} className="flex-1 min-w-[110px]">
            <Text variant="label">unit</Text>
            <select value={edit.volumeUnit} onChange={e => setEdit({ ...edit, volumeUnit: e.target.value })} className="h-9 hairline rounded-sm px-2 bg-bg text-base">
              {['bbl','MT','gal','mmbtu','mwh','units'].map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </Stack>
          <Stack gap={0.5} className="flex-1 min-w-[120px]">
            <Text variant="label">frequency</Text>
            <select value={edit.contractFrequency} onChange={e => setEdit({ ...edit, contractFrequency: e.target.value })} className="h-9 hairline rounded-sm px-2 bg-bg text-base">
              {['one-off','daily','weekly','monthly','quarterly','annually'].map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </Stack>
          <Stack gap={0.5} className="flex-1 min-w-[110px]">
            <Text variant="label">contract months</Text>
            <Input type="number" value={edit.contractDurationMonths} onChange={e => setEdit({ ...edit, contractDurationMonths: e.target.value })} />
          </Stack>
        </Row>
      </Panel>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <PriceLegEditor title="What we buy it for" leg={edit.buyLeg} setLeg={(l) => setEdit({ ...edit, buyLeg: l })} unit={edit.volumeUnit} currency={ccy} />
        <PriceLegEditor title="What we sell it for" leg={edit.sellLeg} setLeg={(l) => setEdit({ ...edit, sellLeg: l })} unit={edit.volumeUnit} currency={ccy} />
      </div>

      <Panel title="Spread → profit (live preview)">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <KV label="buy effective" value={`${ccy} ${fmt2(buyEff)}/${edit.volumeUnit}`} />
          <KV label="sell effective" value={`${ccy} ${fmt2(sellEff)}/${edit.volumeUnit}`} />
          <KV label="profit per unit" value={`${liveSpread >= 0 ? '+' : ''}${ccy} ${fmt2(liveSpread)}/${edit.volumeUnit}`} accent />
          <KV label="margin %" value={sellEff > 0 ? `${fmt((liveSpread / sellEff) * 100, 2)}%` : '—'} accent />
        </div>
        <Row gap={1} className="mt-2">
          <Button variant="primary" size="sm" onClick={patchEcon} disabled={busy}>{busy ? 'saving…' : 'update economics'}</Button>
          <Text variant="label">the saved values feed every period table below</Text>
        </Row>
      </Panel>

      {isVolume && econ ? (
        <>
          <Panel title="Per shipment">
            <div className="grid grid-cols-4 gap-2">
              <KV label="revenue (sell × volume)" value={`${ccy} ${fmt(econ.perShipment.revenue)}`} />
              <KV label="cost (buy × volume)" value={`${ccy} ${fmt(econ.perShipment.cost)}`} />
              <KV label="profit" value={`${ccy} ${fmt(econ.perShipment.gross)}`} accent />
              <KV label="margin" value={`${fmt(econ.perShipment.marginPct * 100, 2)}%`} accent />
            </div>
          </Panel>

          <Panel title="Period earnings (gross profit)">
            <div className="grid grid-cols-3 gap-2">
              <KV label="weekly"   value={`${ccy} ${fmt(econ.weekly.gross)}`} />
              <KV label="monthly"  value={`${ccy} ${fmt(econ.monthly.gross)}`} />
              <KV label="annual"   value={`${ccy} ${fmt(econ.annual.gross)}`} accent />
            </div>
            <Divider className="my-3" />
            <Row justify="between"><Text variant="small">Annual revenue</Text><span className="tnum text-sm">{ccy} {fmt(econ.annual.revenue)}</span></Row>
            <Row justify="between"><Text variant="small">Annual cost</Text><span className="tnum text-sm">{ccy} {fmt(econ.annual.cost)}</span></Row>
            {econ.contractTotal.monthsRemaining > 0 && (
              <>
                <Divider className="my-3" />
                <Row justify="between"><Text variant="small" className="font-medium">Contract total ({econ.contractTotal.monthsRemaining} months)</Text><span className="tnum text-sm font-medium">{ccy} {fmt(econ.contractTotal.gross)} profit</span></Row>
              </>
            )}
          </Panel>

          <Panel title="Per-friend split (gross profit)">
            <div className="grid grid-cols-5 text-xs font-medium uppercase tracking-[0.08em] text-muted px-2 py-1 hairline-b">
              <span>friend</span><span className="text-right tnum">per shipment</span><span className="text-right tnum">weekly</span><span className="text-right tnum">monthly</span><span className="text-right tnum">annual</span>
            </div>
            {econ.splits.map((sp: any) => {
              const f = friendById(sp.friendId);
              return f ? (
                <div key={sp.friendId} className="grid grid-cols-5 px-2 py-2 hairline-b last:border-b-0 items-center text-sm">
                  <Row gap={1}><Avatar name={f.name} friendId={f.id} hasAvatar={!!f.avatarUrl} size="sm" /><Stack gap={0.5}><Text variant="small">{f.name}</Text><Text variant="label">{sp.role} · {(sp.splitBp/100).toFixed(0)}%</Text></Stack></Row>
                  <span className="text-right tnum">{ccy} {fmt(sp.perShipment)}</span>
                  <span className="text-right tnum">{ccy} {fmt(sp.weekly)}</span>
                  <span className="text-right tnum">{ccy} {fmt(sp.monthly)}</span>
                  <span className="text-right tnum font-medium">{ccy} {fmt(sp.annually)}</span>
                </div>
              ) : null;
            })}
            {econ.contractTotal.monthsRemaining > 0 && (
              <div className="px-2 py-2 hairline-t">
                <Text variant="micro" className="mb-1">contract total per friend</Text>
                {econ.splits.map((sp: any) => {
                  const f = friendById(sp.friendId);
                  return f ? (
                    <Row key={sp.friendId} justify="between" className="py-1">
                      <Text variant="small">{f.name}</Text>
                      <span className="tnum text-sm font-medium">{ccy} {fmt(sp.contractTotal)}</span>
                    </Row>
                  ) : null;
                })}
              </div>
            )}
          </Panel>
        </>
      ) : (
        <Panel title="Earnings"><EmptyState title="no contract economics yet" hint="enter volume, buy price, sell price, and frequency above to see profit per unit and period earnings broken out by friend" /></Panel>
      )}

      <Panel title="Realized P&L (from ledger)">
        <Stack gap={2}>
          <Row justify="between"><Text variant="small">Expected revenue</Text>{expectedRevenue > 0 ? <Money amount={expectedRevenue} currency={ccy} className="text-sm" /> : <Text variant="small" muted>—</Text>}</Row>
          <Row justify="between"><Text variant="small">Costs to date</Text><Money amount={-totalCosts} currency={ccy} sign className="text-sm" /></Row>
          <Divider />
          <Row justify="between"><Text variant="small" className="font-medium">Net</Text><Money amount={expectedRevenue - totalCosts} currency={ccy} className="text-sm font-medium" /></Row>
        </Stack>
      </Panel>

      <Panel title="Costs ledger">
        {(ledger ?? []).length === 0 ? <Text variant="label" muted>no costs logged yet</Text> :
          <List>{ledger.map((l: any) => { const payer = friendById(l.paidByFriendId); return (
            <ListItem key={l.id}>
              <Row justify="between"><Stack gap={0.5}><Text variant="small">{l.description}</Text><Text variant="label">{payer?.name ?? '—'} · {l.date}</Text></Stack><Money amount={l.amount} currency={l.currency} className="text-sm" /></Row>
            </ListItem>
          ); })}</List>}
        <Divider className="my-3" />
        <Stack gap={1}>
          <Text variant="micro">log cost</Text>
          <Row gap={1}>
            <Input value={newCost.description} onChange={e => setNewCost({ ...newCost, description: e.target.value })} placeholder="description" className="flex-1" />
            <Input type="number" value={newCost.amount} onChange={e => setNewCost({ ...newCost, amount: e.target.value })} placeholder="amount" className="w-32" />
            <Input type="date" value={newCost.date} onChange={e => setNewCost({ ...newCost, date: e.target.value })} className="w-40" />
            <Button variant="primary" size="sm" onClick={addCost}>add</Button>
          </Row>
        </Stack>
      </Panel>
    </Stack>
  );
}

function PriceLegEditor({ title, leg, setLeg, unit, currency }: { title: string; leg: LegEdit; setLeg: (l: LegEdit) => void; unit: string; currency: string }) {
  const eff = legEffective(leg);
  return (
    <Panel title={title}>
      <Stack gap={2}>
        <Row gap={1}>
          <button onClick={() => setLeg({ ...leg, basis: 'fixed' })} className={`flex-1 text-left px-2 py-2 rounded-sm hairline transition-colors ${leg.basis === 'fixed' ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink'}`}>
            <div className="text-sm font-medium">fixed</div>
            <div className="text-[10px] opacity-70">flat price / {unit}</div>
          </button>
          <button onClick={() => setLeg({ ...leg, basis: 'platts' })} className={`flex-1 text-left px-2 py-2 rounded-sm hairline transition-colors ${leg.basis === 'platts' ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink'}`}>
            <div className="text-sm font-medium">platts +/-</div>
            <div className="text-[10px] opacity-70">benchmark + differential</div>
          </button>
        </Row>
        {leg.basis === 'fixed' ? (
          <Stack gap={0.5}>
            <Text variant="label">price / {unit}</Text>
            <Input type="number" step="0.01" value={leg.fixed} onChange={e => setLeg({ ...leg, fixed: e.target.value })} placeholder="e.g. 82.50" />
          </Stack>
        ) : (
          <Stack gap={2}>
            <PlattsPicker
              benchmark={leg.benchmark}
              code={leg.code}
              onChange={({ benchmark, code }) => setLeg({ ...leg, benchmark, code })}
            />
            <Row gap={1}>
              <Stack gap={0.5} className="flex-1">
                <Text variant="label">benchmark price ({currency}/{unit})</Text>
                <Input type="number" step="0.01" value={leg.benchmarkPrice} onChange={e => setLeg({ ...leg, benchmarkPrice: e.target.value })} placeholder="e.g. 82.50" />
              </Stack>
              <Stack gap={0.5} className="flex-1">
                <Text variant="label">+/- differential</Text>
                <Input type="number" step="0.01" value={leg.differential} onChange={e => setLeg({ ...leg, differential: e.target.value })} placeholder="e.g. -0.50" />
              </Stack>
            </Row>
          </Stack>
        )}
        <Divider />
        <Row justify="between">
          <Text variant="micro">effective</Text>
          <Text variant="body" className="tnum font-medium">{currency} {fmt2(eff)} / {unit}</Text>
        </Row>
      </Stack>
    </Panel>
  );
}

function KV({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`hairline rounded-sm p-2 ${accent ? 'bg-whisper' : ''}`}>
      <Text variant="label">{label}</Text>
      <div className={`tnum text-sm ${accent ? 'font-medium' : ''}`}>{value}</div>
    </div>
  );
}
